import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import ChapterHeader from "@/components/ChapterHeader";
import EditorialMarquee from "@/components/EditorialMarquee";
import UploadedPhotos from "@/components/UploadedPhotos";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useCategoryPhotos, pickPhoto } from "@/lib/usePhotos";
import { CalendarIcon, Users, Utensils, Music, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENTS = [
  { value: "wedding", label: "Wedding" },
  { value: "reception", label: "Reception" },
  { value: "engagement", label: "Engagement" },
  { value: "corporate", label: "Corporate retreat / off-site" },
  { value: "conference", label: "Conference" },
  { value: "birthday", label: "Birthday" },
  { value: "anniversary", label: "Anniversary" },
  { value: "other", label: "Other" },
];

const GUEST_BUCKETS = [
  "Up to 50",
  "50 – 100",
  "100 – 250",
  "250 – 500",
  "500+",
];

const FACILITIES = [
  { icon: Users, title: "Seats up to 1500", desc: "Sit-down banquets under the mango canopy or inside the heritage hall." },
  { icon: Utensils, title: "Customised catering", desc: "Indian, Tandoor, Chinese, Continental." },
  { icon: Music, title: "Sound & lighting", desc: "Full setup for weddings, receptions and corporate retreats." },
  { icon: Flame, title: "Decor", desc: "As per demand and budget." },
];

export default function Banquet() {
  const banquet = useCategoryPhotos("banquet");
  const heritage = useCategoryPhotos("heritage");
  const activity = useCategoryPhotos("activity");
  const heroImg = pickPhoto(banquet, 0) || pickPhoto(heritage, 0);
  const secondaryImg = pickPhoto(banquet, 1) || pickPhoto(heritage, 1) || pickPhoto(activity, 0);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "wedding",
    guest_bucket: "Up to 50",
    message: "",
  });
  const [eventDate, setEventDate] = useState(undefined);
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    const eventLabel = EVENTS.find((x) => x.value === form.event_type)?.label || form.event_type;
    const dateStr = eventDate ? format(eventDate, "d MMMM yyyy") : "not specified";
    const messageBody =
      `Event type: ${eventLabel}\n` +
      `Expected date: ${dateStr}\n` +
      `Guests: ${form.guest_bucket}\n` +
      `--\n${form.message || "(no additional notes)"}`;
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: `Banquet enquiry — ${eventLabel}`,
        message: messageBody,
      });
      toast.success("Enquiry received. We will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", event_type: "wedding", guest_bucket: "Up to 50", message: "" });
      setEventDate(undefined);
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="banquet-page" className="pt-32 pb-24">
      {/* Hero */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-8">Vol · IX · Banquets</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0] max-w-5xl">
            Weddings, receptions, retreats — <em className="italic text-[#D97645]">on a working estate</em>.
          </h1>
          <p className="mt-8 max-w-2xl text-[#B8A891] text-lg leading-relaxed">
            The heritage hall, the orchard lawns, and the private Sal forest — a setting most banquet
            venues in Ranchi cannot offer. Sit-down dinners under the mango canopy. Bonfires by the
            1930's fireplace. Everything from soil to plate cooked on-site.
          </p>
        </FadeIn>
      </section>

      {/* Split visual */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <FadeIn className="lg:col-span-8">
          <div className="w-full aspect-[16/10] bg-[#27221C] overflow-hidden">
            {heroImg && <img src={heroImg} alt="Banquet hall" className="w-full h-full object-cover vintage-img" />}
          </div>
        </FadeIn>
        <FadeIn delay={0.15} className="lg:col-span-4">
          <div className="w-full aspect-[3/4] bg-[#27221C] overflow-hidden">
            {secondaryImg && <img src={secondaryImg} alt="Estate grounds" className="w-full h-full object-cover vintage-img" />}
          </div>
        </FadeIn>
      </section>

      {/* Facilities grid */}
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-10">
        <ChapterHeader num="01" kicker="What we offer" title="Every part of the estate, at your disposal." />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {FACILITIES.map((f, i) => (
            <FadeIn key={f.title} delay={i * 0.08}>
              <div className="border border-white/10 p-6 h-full" data-testid={`facility-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <f.icon size={22} className="text-[#D97645]" />
                <div className="mt-6 font-display text-2xl text-[#F2E7D0]">{f.title}</div>
                <p className="mt-3 text-sm text-[#B8A891] leading-relaxed">{f.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <div className="my-16">
        <EditorialMarquee items={["Weddings", "Receptions", "Corporate Retreats", "Anniversaries", "Since 1930's"]} />
      </div>

      {/* Enquiry form */}
      <section id="enquire" className="mx-auto max-w-[1600px] px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <FadeIn className="lg:col-span-5">
          <ChapterHeader num="02" kicker="Enquire" title="Send us the details." />
          <p className="text-[#B8A891] leading-relaxed">
            Share the event, expected headcount and a rough date. Someone from the estate will
            revert within one working day with a custom package, tasting menu options and a hall walk-through.
          </p>
          <div className="mt-8 space-y-3 text-sm text-[#B8A891]">
            <div>
              <span className="font-mono-label text-[#D97645]">Direct call · </span>
              <a href="tel:+917004810637" className="text-[#F2E7D0] hover:text-[#D97645] transition">+91 7004810637</a>
              <span className="text-[#B8A891]/50 mx-2">|</span>
              <a href="tel:+919792920905" className="text-[#F2E7D0] hover:text-[#D97645] transition">+91 9792920905</a>
            </div>
            <div>
              <span className="font-mono-label text-[#D97645]">Email · </span>
              <a href="mailto:Rahul@tikratoliestate.com" className="text-[#F2E7D0] hover:text-[#D97645] transition">Rahul@tikratoliestate.com</a>
              <span className="text-[#B8A891]/50 mx-2">|</span>
              <a href="mailto:tikratolibookings@gmail.com" className="text-[#F2E7D0] hover:text-[#D97645] transition">tikratolibookings@gmail.com</a>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.1} className="lg:col-span-7">
          <form data-testid="banquet-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="font-mono-label text-[#B8A891]">Name</Label>
              <Input data-testid="banquet-name" value={form.name} onChange={change("name")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Email</Label>
              <Input data-testid="banquet-email" type="email" value={form.email} onChange={change("email")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Phone</Label>
              <Input data-testid="banquet-phone" value={form.phone} onChange={change("phone")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Event type</Label>
              <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="banquet-event-type" className="mt-2 bg-transparent border-white/20 rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                  {EVENTS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Expected date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="banquet-date-btn"
                    className="mt-2 w-full h-12 bg-transparent border border-white/20 px-4 flex items-center justify-between text-left text-[#F2E7D0]"
                  >
                    <span className="text-sm">
                      {eventDate ? format(eventDate, "d MMMM yyyy") : "Pick a date"}
                    </span>
                    <CalendarIcon size={16} className="text-[#B8A891]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="bg-[#27221C] border border-white/20 rounded-none" align="start">
                  <Calendar
                    mode="single"
                    selected={eventDate}
                    onSelect={setEventDate}
                    disabled={{ before: new Date() }}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="font-mono-label text-[#B8A891]">Guests</Label>
              <Select value={form.guest_bucket} onValueChange={(v) => setForm({ ...form, guest_bucket: v })}>
                <SelectTrigger data-testid="banquet-guests" className="mt-2 bg-transparent border-white/20 rounded-none h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#27221C] border-white/20 rounded-none">
                  {GUEST_BUCKETS.map((g) => <SelectItem key={g} value={g}>{g} guests</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891]">Notes (theme, cuisine preferences, accommodation, etc.)</Label>
              <Textarea data-testid="banquet-message" rows={5} value={form.message} onChange={change("message")} className="mt-2 bg-transparent border-white/20 rounded-none" />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                data-testid="banquet-submit"
                disabled={loading}
                className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-14 px-8"
              >
                {loading ? "Sending…" : "Send banquet enquiry"}
              </Button>
            </div>
          </form>
        </FadeIn>
      </section>
      {/* Banquet-category uploaded gallery */}
      <UploadedPhotos category="banquet" kicker="Banquet Facilities" title="Halls, decor and setups." />
      {/* Past events archive */}
      <UploadedPhotos category="past_events" kicker="Past Events" title="Weddings, receptions and retreats — hosted at Tikratoli." />
    </main>
  );
}
